// Admin Face ID / Touch ID sign-in (passkeys) — the browser half. The server
// half and its safety rules live in the server repo (lib/admin/passkeys.js):
// Face ID is only ever added right after a full password + code sign-in, and
// password + code always stays as the backup door.
//
// Two notes kept on this device only (each read and write is guarded — a
// private window or blocked storage just means "no note"):
//   adm-faceid-here       Face ID worked on this device → lead with it
//   adm-faceid-dismissed  "Not now" was tapped here → don't offer it again
import {
  browserSupportsWebAuthn,
  platformAuthenticatorIsAvailable,
  startAuthentication,
  startRegistration,
} from "@simplewebauthn/browser";

export { startAuthentication, startRegistration };

const HERE = "adm-faceid-here";
const DISMISSED = "adm-faceid-dismissed";

function read(key: string): boolean {
  try {
    return localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}
function write(key: string, on: boolean) {
  try {
    if (on) localStorage.setItem(key, "1");
    else localStorage.removeItem(key);
  } catch {
    /* nothing to remember then */
  }
}

export const faceIdWorksHere = () => read(HERE);
export const rememberFaceIdHere = (on = true) => write(HERE, on);
export const faceIdOfferDismissed = () => read(DISMISSED);
export const dismissFaceIdOffer = () => write(DISMISSED, true);

/** The browser can do passkeys at all (a sign-in button makes sense). */
export function canUseFaceId(): boolean {
  try {
    return browserSupportsWebAuthn();
  } catch {
    return false;
  }
}

/** This device has a built-in Face ID / Touch ID / fingerprint to add. */
export async function canAddFaceIdHere(): Promise<boolean> {
  if (!canUseFaceId()) return false;
  try {
    return await platformAuthenticatorIsAvailable();
  } catch {
    return false;
  }
}

/** What the device calls it: Face ID on iPhone and iPad, Touch ID on a Mac. */
export function faceIdName(ua = typeof navigator !== "undefined" ? navigator.userAgent : ""): string {
  const touchMac = /Macintosh/.test(ua) && typeof navigator !== "undefined" && navigator.maxTouchPoints > 1; // iPadOS asks for the desktop site
  if (/iPhone|iPad|iPod/.test(ua) || touchMac) return "Face ID";
  if (/Macintosh/.test(ua)) return "Touch ID";
  return "Face ID";
}

/** A short device name for the admin's list ("iPhone", "Mac", …). */
export function deviceLabel(ua = typeof navigator !== "undefined" ? navigator.userAgent : ""): string {
  if (/iPhone/.test(ua)) return "iPhone";
  if (/iPad/.test(ua)) return "iPad";
  if (/Macintosh/.test(ua)) return "Mac";
  if (/Android/.test(ua)) return "Android";
  if (/Windows/.test(ua)) return "Windows";
  return "This device";
}

/** The person closed the system sheet or let it time out — not an error. */
export function faceIdCancelled(err: unknown): boolean {
  const e = err as { name?: string; code?: string; cause?: { name?: string } } | null;
  return e?.name === "NotAllowedError" || e?.name === "AbortError" || e?.code === "ERROR_CEREMONY_ABORTED" || e?.cause?.name === "NotAllowedError";
}

/** This device already holds a Face ID for this account. */
export function faceIdAlreadyHere(err: unknown): boolean {
  const e = err as { name?: string; code?: string } | null;
  return e?.code === "ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED" || e?.name === "InvalidStateError";
}
